// client/src/components/pages/Geofencing/ZoneAlertsSwitch.tsx (ST-00282)
//
// The zone's Alerts switch, as the zone editor and the zone list show it. On: the zone's rules
// say what alerts. Off: the zone records crossings but raises no alert. A no-go zone's switch is
// locked on (`locked`): it shows on, with a lock, cannot be pressed, and says why. The switch only
// reports the user's choice (onChange); the server decides and enforces.
import { FaLock } from 'react-icons/fa';

export interface ZoneAlertsSwitchProps {
  /** On. */
  checked: boolean;
  /** A no-go zone: always on; shown with a lock and `lockedReason`. */
  locked?: boolean;
  lockedReason?: string;
  /** Busy or not allowed (e.g. a save in flight, no geofencing.manage). */
  disabled?: boolean;
  onChange?: (on: boolean) => void;
  /** Accessible name, e.g. "Alerts for Pit A". */
  label: string;
  size?: 'sm' | 'md';
  /** Show the On / Off word beside the switch. */
  showState?: boolean;
}

export default function ZoneAlertsSwitch({
  checked,
  locked = false,
  lockedReason,
  disabled = false,
  onChange,
  label,
  size = 'md',
  showState = true,
}: ZoneAlertsSwitchProps) {
  const on = locked || checked;
  const inert = locked || disabled || !onChange;
  const track = size === 'sm' ? 'h-5 w-9' : 'h-6 w-11';
  const knob = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const shift = size === 'sm' ? 'translate-x-4' : 'translate-x-5';
  return (
    <span className="inline-flex items-center gap-2" title={locked ? lockedReason : undefined}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        aria-disabled={inert || undefined}
        disabled={disabled && !locked}
        data-locked={locked || undefined}
        onClick={() => {
          if (!inert) onChange?.(!on);
        }}
        className={`relative inline-flex ${track} flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1 ${
          on ? 'bg-teal-500' : 'bg-slate-300'
        } ${inert ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}`}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-flex items-center justify-center ${knob} transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
            on ? shift : 'translate-x-0'
          }`}
        >
          {locked && <FaLock className="text-teal-600" size={size === 'sm' ? 7 : 8} />}
        </span>
      </button>
      {showState && (
        <span className={`text-xs font-medium ${on ? 'text-teal-700' : 'text-slate-500'}`}>
          {on ? 'On' : 'Off'}
          {locked && <span className="sr-only"> (locked: {lockedReason})</span>}
        </span>
      )}
    </span>
  );
}
